import React, { useState, useRef, useCallback } from 'react';
import { Upload, X } from 'lucide-react';
import { useI18n } from '../../../../contexts/I18nContext';
import imageService from '../../../../services/imageService';
import toast from 'react-hot-toast';
import ErrorModal from '../../../common/ErrorModal';
import './ImageUpload.scss';

const ImageUpload = ({
  id,
  title,
  description,
  required = false,
  value = null,
  onChange,
  onError,
  props = {},
  height
}) => {
  const { t } = useI18n();
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // 从props中获取配置，设置默认值
  const {
    acceptedFormats = ['jpg', 'jpeg', 'png', 'gif'],
    maxSize = 10 * 1024 * 1024,
    preview = true,
    multiple = false,
    placeholder = null,
    maxFiles = 10,
    previewHeight = undefined,
    gridItemHeight = undefined,
    uploadAreaHeight = 120,
    uploadAreaPadding = undefined
  } = props;

  const effectiveSingleHeight = previewHeight || height || 300;
  const effectiveGridItemHeight = gridItemHeight || 80;
  const isMaxReached = multiple && Array.isArray(value) && value.length >= maxFiles;

  // 验证文件
  const validateFile = useCallback((file) => {
    const validation = imageService.validateImageFile(file);
    if (!validation.valid) {
      onError?.(validation.error);
      setShowErrorModal(true);
      return false;
    }
    return true;
  }, [onError]);

  // 处理文件选择
  const handleFileSelect = useCallback(async (files) => {
    let fileList = Array.from(files);
    
    if (!multiple && fileList.length > 1) {
      const error = t('aiToolTemplate.imageUpload.validation.maxFiles', { max: 1 });
      onError?.(error);
      toast.error(error);
      return;
    }

    if (multiple) {
      const existingCount = Array.isArray(value) ? value.length : 0;
      if (existingCount + fileList.length > maxFiles) {
        const allowed = Math.max(0, maxFiles - existingCount);
        if (allowed <= 0) {
          const error = t('aiToolTemplate.imageUpload.validation.maxFiles', { max: maxFiles });
          onError?.(error);
          toast.error(error);
          return;
        }
        fileList = fileList.slice(0, allowed);
        const warn = t('aiToolTemplate.imageUpload.validation.maxFiles', { max: maxFiles });
        toast.error(warn);
      }
    }

    const validFiles = [];
    for (const file of fileList) {
      if (validateFile(file)) {
        validFiles.push(file);
      }
    }

    if (validFiles.length === 0) return;

    setIsUploading(true);
    
    try {
      if (multiple) {
        // 多文件上传
        const uploadedFiles = [];
        for (const file of validFiles) {
          const reader = new FileReader();
          const result = await new Promise((resolve) => {
            reader.onload = (e) => resolve({
              file,
              url: e.target.result,
              name: file.name,
              size: file.size
            });
            reader.readAsDataURL(file);
          });
          uploadedFiles.push(result);
        }
        const base = Array.isArray(value) ? value : [];
        const merged = [...base, ...uploadedFiles].slice(0, maxFiles);
        onChange?.(merged);
      } else {
        // 单文件上传
        const file = validFiles[0];
        const reader = new FileReader();
        reader.onload = (e) => {
          const result = {
            file,
            url: e.target.result,
            name: file.name,
            size: file.size
          };
          onChange?.(result);
        };
        reader.readAsDataURL(file);
      }
    } catch (error) {
      const errorMsg = t('aiToolTemplate.imageUpload.validation.uploadFailed');
      onError?.(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsUploading(false);
    }
  }, [multiple, validateFile, onChange, onError, t, maxFiles, value]);

  // 处理拖拽事件
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  // 处理点击上传
  const handleClick = useCallback(() => {
    if (isMaxReached) {
      const warn = t('aiToolTemplate.imageUpload.validation.maxFiles', { max: maxFiles });
      toast.error(warn);
      return;
    }
    fileInputRef.current?.click();
  }, [isMaxReached, maxFiles, t]);

  // 处理文件输入变化
  const handleInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  // 移除文件
  const handleRemove = useCallback((index = null) => {
    if (multiple && index !== null) {
      // 移除特定文件
      const newFiles = [...value];
      newFiles.splice(index, 1);
      onChange?.(newFiles);
    } else {
      // 移除单个文件或所有文件
      onChange?.(null);
    }
  }, [multiple, value, onChange]);

  // 渲染上传区域
  const renderUploadArea = () => (
    <div
      className={`image-upload-area ${isDragging ? 'dragging' : ''} ${isUploading ? 'uploading' : ''} ${uploadAreaHeight ? 'compact-area' : ''} ${isMaxReached ? 'disabled' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      style={uploadAreaHeight ? { minHeight: uploadAreaHeight, padding: uploadAreaPadding || '12px 12px' } : undefined}
    >
      <div className="upload-content">
        <div className="upload-icon">
          {isUploading ? (
            <div className="loading-spinner" />
          ) : (
            <Upload size={uploadAreaHeight && uploadAreaHeight <= 160 ? 36 : 48} />
          )}
        </div>
        
        <h3>{isMaxReached ? t('aiToolTemplate.imageUpload.validation.maxFiles', { max: maxFiles }) : (isUploading ? t('aiToolTemplate.imageUpload.uploading') : (placeholder || t('aiToolTemplate.imageUpload.clickToUpload')))}</h3>
        <p>{description || t('aiToolTemplate.imageUpload.defaultDescription')}</p>
        <p className="size-limit">{t('aiToolTemplate.imageUpload.validation.fileSize', { maxSize: Math.round(maxSize / 1024 / 1024) })}</p>
      </div>
    </div>
  );

  // 渲染大图预览
  const renderPreviewOverlay = () => {
    if (!previewImage) return null;

    return (
      <div className="preview-overlay" onClick={() => setPreviewImage(null)}>
        <button className="close-button" onClick={() => setPreviewImage(null)}>
          <X size={32} />
        </button>
        <div className="preview-content" onClick={e => e.stopPropagation()}>
          <img src={previewImage} alt="Preview" />
        </div>
      </div>
    );
  };

  // 渲染预览
  const renderPreview = () => {
    if (!preview || !value) return null;

    if (multiple && Array.isArray(value)) {
      return (
        <div className="image-preview-grid">
          {value.map((item, index) => (
            <div 
              key={index} 
              className="preview-item" 
              onClick={() => setPreviewImage(item.url)}
              style={{ cursor: 'pointer' }}
            >
              <img src={item.url} alt={item.name} style={{ height: effectiveGridItemHeight }} />
              <button
                className="remove-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(index);
                }}
              >
                <X size={16} />
              </button>
              <div className="file-info">
                <span className="file-name">{item.name}</span>
                <span className="file-size">{Math.round(item.size / 1024)}KB</span>
              </div>
            </div>
          ))}
        </div>
      );
    } else if (value && value.url) {
      return (
        <div className="image-preview">
          <div 
            className="preview-container" 
            onClick={() => setPreviewImage(value.url)}
            style={{ cursor: 'pointer' }}
          >
            <img src={value.url} alt={value.name} style={{ height: effectiveSingleHeight }} />
            <button
              className="remove-button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
            >
              <X size={20} />
            </button>
          </div>
          <div className="file-info">
            <span className="file-name">{value.name}</span>
            <span className="file-size">{Math.round(value.size / 1024)}KB</span>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="image-upload-component">
      <div className="component-header">
        <h3 className="component-title">
          {title}
          {required && <span className="required-mark">*</span>}
        </h3>
        {description && <p className="component-description">{description}</p>}
      </div>

      <div className="upload-container">
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedFormats.map(format => `.${format}`).join(',')}
          multiple={multiple}
          onChange={handleInputChange}
          style={{ display: 'none' }}
        />
        {(!value || (multiple && (!value || value.length === 0))) && renderUploadArea()}
        {renderPreview()}
        
        {/* 如果已有文件但支持多选，显示添加更多按钮 */}
        {multiple && value && value.length > 0 && (
          <button className="add-more-button" onClick={handleClick} disabled={isMaxReached}>
            <Upload size={16} />
            {t('aiToolTemplate.imageUpload.addMore')}
          </button>
        )}
        <ErrorModal
          open={showErrorModal}
          title={t('common.warning')}
          message={`${t('imageService.unsupportedFormat')}\n${t('imageService.fileTooLarge')}`}
          onClose={() => setShowErrorModal(false)}
        />
        {renderPreviewOverlay()}
      </div>
    </div>
  );
};

export default ImageUpload;
